import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Megaphone, Upload, Trash2, ImageIcon, PlusCircle } from "lucide-react";

const MAX_IMAGE_BYTES = 500 * 1024; // 500 KB
const MAX_SPONSORS = 3;

/**
 * Editor-only card to configure the sponsorship panel that replaces the
 * stadium hero on the /register page. Everything (logo + up to 3 sponsor
 * images) is stored as base64 data URLs on the backend — small enough for
 * MongoDB and served inline to unauthenticated visitors.
 */
export default function RegistrationSponsorshipCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({
    enabled: false,
    message: "",
    organisation_logo_url: null,
    sponsors: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/registration-sponsorship");
      setCfg({
        enabled: !!data.enabled,
        message: data.message || "",
        organisation_logo_url: data.organisation_logo_url || null,
        // Stamp a stable client-side _key on each row so React reconciles
        // correctly when sponsors are added or removed (index-as-key would
        // shift local state onto the wrong row after a delete).
        sponsors: (Array.isArray(data.sponsors) ? data.sponsors : []).map((s) => ({
          ...s,
          _key: s._key || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
        })),
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      if (file.size > MAX_IMAGE_BYTES) {
        reject(new Error(`Image is ${(file.size / 1024).toFixed(0)} KB — please keep it under 500 KB.`));
        return;
      }
      if (!file.type.startsWith("image/")) {
        reject(new Error("File must be an image (PNG, JPG, WEBP or GIF)."));
        return;
      }
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(new Error("Couldn't read image"));
      r.readAsDataURL(file);
    });

  const onLogoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data_url = await fileToDataUrl(f);
      setField({ organisation_logo_url: data_url });
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = "";
    }
  };
  const clearLogo = () => setField({ organisation_logo_url: null });

  const addSponsorSlot = () => {
    if (cfg.sponsors.length >= MAX_SPONSORS) return;
    setCfg((c) => ({
      ...c,
      sponsors: [
        ...c.sponsors,
        {
          _key: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
          image_url: "",
          target_url: "",
          alt_text: "",
        },
      ],
    }));
  };

  const updateSponsor = (idx, patch) =>
    setCfg((c) => ({
      ...c,
      sponsors: c.sponsors.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));

  const removeSponsor = (idx) =>
    setCfg((c) => ({ ...c, sponsors: c.sponsors.filter((_, i) => i !== idx) }));

  const onSponsorImageUpload = async (idx, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data_url = await fileToDataUrl(f);
      updateSponsor(idx, { image_url: data_url });
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = "";
    }
  };

  const save = async () => {
    // Frontend guard: sponsors need an image at minimum
    const cleaned = cfg.sponsors.filter((s) => s.image_url);
    if (cfg.enabled && !cfg.message && !cfg.organisation_logo_url && cleaned.length === 0) {
      toast.error("Add a message, a logo or at least one sponsor image before enabling.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/registration-sponsorship", {
        enabled: !!cfg.enabled,
        message: cfg.message?.trim() || null,
        organisation_logo_url: cfg.organisation_logo_url || null,
        sponsors: cleaned.map((s) => ({
          image_url: s.image_url,
          target_url: s.target_url?.trim() || null,
          alt_text: s.alt_text?.trim() || null,
        })),
      });
      setCfg({
        enabled: !!data.enabled,
        message: data.message || "",
        organisation_logo_url: data.organisation_logo_url || null,
        sponsors: (data.sponsors || []).map((s) => ({
          ...s,
          _key: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
        })),
      });
      toast.success("Sponsorship configuration saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="border border-zinc-200 rounded-sm bg-white p-6"
      data-testid="registration-sponsorship-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Megaphone className="h-4 w-4 text-zinc-500" />
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">
              Register page sponsorship
            </h2>
          </div>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Replace the football-stadium hero on <code className="text-xs">/register</code> with
            a message, your organisation logo and up to three clickable sponsor images.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            {cfg.enabled ? "Sponsorship is showing" : "Default stadium hero"}
          </span>
          <Switch
            checked={cfg.enabled}
            onCheckedChange={(v) => setField({ enabled: v })}
            disabled={loading || saving}
            data-testid="sponsorship-enabled-toggle"
            aria-label="Toggle registration sponsorship"
          />
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</p>
      ) : (
        <div className="mt-8 space-y-8">
          {/* Message ---------------------------------------------------- */}
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              Message (optional · max 280 chars)
            </Label>
            <Textarea
              value={cfg.message}
              onChange={(e) => setField({ message: e.target.value.slice(0, 280) })}
              placeholder="e.g. Proudly sponsored by The Founders Arms — join us every Saturday for match-day pints."
              data-testid="sponsorship-message"
              className="mt-2 rounded-sm border-zinc-300 min-h-[80px]"
            />
            <p className="mt-1 text-[11px] text-zinc-400 tabular-nums">
              {cfg.message?.length || 0} / 280
            </p>
          </div>

          {/* Organisation logo ----------------------------------------- */}
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              Organisation / company logo (optional)
            </Label>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {cfg.organisation_logo_url ? (
                <div className="relative">
                  <img
                    src={cfg.organisation_logo_url}
                    alt="Organisation logo preview"
                    data-testid="sponsorship-logo-preview"
                    className="h-20 w-20 rounded-sm object-contain border border-zinc-200 bg-white"
                  />
                </div>
              ) : (
                <div className="h-20 w-20 rounded-sm border border-dashed border-zinc-300 inline-flex items-center justify-center text-zinc-400">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="h-9 px-3 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 cursor-pointer inline-flex items-center text-xs font-semibold">
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  {cfg.organisation_logo_url ? "Replace" : "Upload logo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onLogoUpload}
                    data-testid="sponsorship-logo-input"
                    className="hidden"
                  />
                </label>
                {cfg.organisation_logo_url && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearLogo}
                    data-testid="sponsorship-logo-clear"
                    className="h-9 px-3 rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 text-xs"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">
              PNG or SVG works best. Max 500 KB. Shown at the top of the sponsorship panel.
            </p>
          </div>

          {/* Sponsor images -------------------------------------------- */}
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Sponsor images ({cfg.sponsors.length} / {MAX_SPONSORS})
              </Label>
              <Button
                type="button"
                variant="outline"
                onClick={addSponsorSlot}
                disabled={cfg.sponsors.length >= MAX_SPONSORS}
                data-testid="sponsorship-add-slot"
                className="h-8 rounded-sm border-zinc-300 text-xs"
              >
                <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> Add sponsor
              </Button>
            </div>

            {cfg.sponsors.length === 0 && (
              <p className="mt-3 text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-sm p-4">
                No sponsors added yet. Click <strong>Add sponsor</strong> to attach up to three clickable images.
              </p>
            )}

            <div className="mt-4 space-y-4">
              {cfg.sponsors.map((s, idx) => (
                <div
                  key={s._key || idx}
                  data-testid={`sponsorship-sponsor-${idx}`}
                  className="border border-zinc-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-[128px_1fr_auto] gap-4"
                >
                  {/* image */}
                  <div>
                    {s.image_url ? (
                      <img
                        src={s.image_url}
                        alt={s.alt_text || `Sponsor ${idx + 1} preview`}
                        className="h-24 w-32 rounded-sm object-contain border border-zinc-200 bg-white"
                      />
                    ) : (
                      <div className="h-24 w-32 rounded-sm border border-dashed border-zinc-300 inline-flex items-center justify-center text-zinc-400">
                        <ImageIcon className="h-6 w-6" />
                      </div>
                    )}
                    <label className="mt-2 h-8 px-2 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 cursor-pointer inline-flex items-center text-[11px] font-semibold w-32 justify-center">
                      <Upload className="h-3 w-3 mr-1.5" />
                      {s.image_url ? "Replace" : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onSponsorImageUpload(idx, e)}
                        data-testid={`sponsorship-sponsor-image-${idx}`}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {/* fields */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                        Link URL (opens on click)
                      </Label>
                      <Input
                        value={s.target_url || ""}
                        onChange={(e) => updateSponsor(idx, { target_url: e.target.value })}
                        placeholder="https://sponsor-website.com"
                        data-testid={`sponsorship-sponsor-target-${idx}`}
                        className="mt-1 h-9 rounded-sm border-zinc-300 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                        Alt text (for accessibility)
                      </Label>
                      <Input
                        value={s.alt_text || ""}
                        onChange={(e) => updateSponsor(idx, { alt_text: e.target.value.slice(0, 120) })}
                        placeholder="e.g. Founders Arms — sponsors of match-day nights"
                        data-testid={`sponsorship-sponsor-alt-${idx}`}
                        className="mt-1 h-9 rounded-sm border-zinc-300 text-sm"
                      />
                    </div>
                  </div>
                  {/* delete */}
                  <div className="flex md:flex-col md:justify-start justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeSponsor(idx)}
                      data-testid={`sponsorship-remove-${idx}`}
                      title="Remove this sponsor"
                      className="h-8 rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 text-xs"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save ----------------------------------------------------- */}
          <div className="pt-4 border-t border-zinc-100 flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="sponsorship-save"
              className="h-10 px-6 rounded-sm bg-zinc-950 hover:bg-zinc-900 text-white font-semibold text-sm"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save sponsorship"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
